import { isNativeAppWebview } from 'core/utils/utils';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import Select, { components as ReactSelectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Icon from './../Icon';
import useSelectCustomizable from './useSelectCustomizable';

export const utilsSelectCustomizable = {
  /**
   * Devuelve las opciones que son necesarias para el componente SelectCustomizable,
   * dichas opciones las compone recorriendo el array elements
   * y accediendo a la propiedad propertyToLabel para el valor label,
   * y a la propieddad propertyToValue para el valor value de las opciones
   * @param {array} elements
   * @param {string} propertyToLabel
   * @param {string} propertyToValue
   */
  getOptionsProperties(elements, propertyToLabel, propertyToValue) {
    return elements && propertyToLabel && propertyToValue
      ? elements.map((element) => {
          return {
            label: element[propertyToLabel],
            value: element[propertyToValue],
          };
        })
      : [];
  },

  /**
   * Detecta si el dispositivo actual es móvil o tablet
   * @returns {boolean} true si es un dispositivo móvil o tablet
   */
  isMobileDevice() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIPad = /ipad/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

    return isIPad || isMobile || isNativeAppWebview();
  },
};

function getControlBorderStyles(isFocused, hasValue, isSearchable, hasError) {
  const hoverBorderColor = hasError
    ? 'var(--select-customizable-border-error)'
    : 'var(--select-customizable-border-active)';

  if (isFocused) {
    return {
      border: '2px solid var(--select-customizable-border-active)',
      '&:hover': { borderColor: hoverBorderColor },
    };
  }
  if (hasError) {
    return {
      border: '2px solid var(--select-customizable-border-error)',
      '&:hover': { borderColor: hoverBorderColor },
    };
  }
  const isActive = hasValue || isSearchable;
  if (isActive) {
    return {
      border: '2px solid var(--select-customizable-border-active)',
      '&:hover': { borderColor: hoverBorderColor },
    };
  }
  return {
    border: '1px solid var(--select-customizable-border-default)',
    '&:hover': {
      borderColor: hoverBorderColor,
      boxShadow: `0 0 0 1px ${hoverBorderColor}`,
    },
  };
}

// Componentes custom definidos a nivel de módulo para mantener identidad de referencia
// entre renders y evitar que react-select los desmonte/remonte en cada keystroke.
// Los datos del componente padre llegan a través de selectProps (react-select pasa
// todas las props desconocidas del <Select> en selectProps de cada custom component).

const AccessibleControl = ({ children, innerProps, menuIsOpen, ...controlProps }) => (
  <ReactSelectComponents.Control innerProps={{ ...innerProps }} menuIsOpen={menuIsOpen} {...controlProps}>
    {children}
  </ReactSelectComponents.Control>
);
AccessibleControl.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  menuIsOpen: PropTypes.bool,
};

const AccessibleInput = ({ selectProps, ...inputProps }) => (
  <ReactSelectComponents.Input
    role="combobox"
    aria-haspopup="listbox"
    aria-expanded={selectProps.menuIsOpen ? 'true' : 'false'}
    aria-controls={selectProps._listboxId}
    aria-activedescendant={selectProps._focusedOptionId || undefined}
    selectProps={selectProps}
    {...inputProps}
  />
);
AccessibleInput.propTypes = {
  selectProps: PropTypes.shape({
    menuIsOpen: PropTypes.bool,
    _listboxId: PropTypes.string,
    _focusedOptionId: PropTypes.string,
  }),
};

const AccessibleMenu = ({ children, innerProps, selectProps, ...menuProps }) => (
  <ReactSelectComponents.Menu
    innerProps={{ ...innerProps, role: 'listbox', id: selectProps._listboxId }}
    selectProps={selectProps}
    {...menuProps}
  >
    {children}
  </ReactSelectComponents.Menu>
);
AccessibleMenu.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.shape({
    _listboxId: PropTypes.string,
  }),
};

const AccessibleOption = ({ children, innerProps, isSelected, isFocused, selectProps, ...optionProps }) => {
  useEffect(() => {
    if (isFocused && innerProps.id) {
      selectProps._onOptionFocusChange(innerProps.id);
    }
  }, [isFocused, innerProps.id]);

  return (
    <ReactSelectComponents.Option
      innerProps={{ ...innerProps, role: 'option', 'aria-selected': isSelected }}
      isSelected={isSelected}
      isFocused={isFocused}
      selectProps={selectProps}
      {...optionProps}
    >
      {children}
    </ReactSelectComponents.Option>
  );
};
AccessibleOption.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.shape({
    id: PropTypes.string,
  }),
  isSelected: PropTypes.bool,
  isFocused: PropTypes.bool,
  selectProps: PropTypes.shape({
    _onOptionFocusChange: PropTypes.func,
  }),
};

const SelectCustomizable = ({
  t,
  isMulti,
  options,
  defaultValue,
  value,
  placeholder = t('Select'),
  noOptionsMessage,
  variant,
  onChange,
  label,
  name,
  size,
  error,
  canCreate,
  onCreateOption,
  menuPosition,
  iconTypeIndicator,
  menuPortalTarget,
  useShortLabels,
  hideSelectedOptions = false,
  showValuesAsList = false,
  isSearchable,
  inputId,
  ...props
}) => {
  const {
    resolvedInputId,
    labelId,
    listboxId,
    selectRef,
    focusedOptionId,
    onOptionFocusChange,
    handleKeyDown,
    handleMenuOpen,
    handleMenuClose,
  } = useSelectCustomizable({ inputId, name });

  // Override isSearchable to be false on mobile devices
  const finalIsSearchable = utilsSelectCustomizable.isMobileDevice() ? false : isSearchable;
  // Force menu to open upwards on mobile devices
  const finalMenuPlacement = utilsSelectCustomizable.isMobileDevice() ? 'top' : 'auto';

  const DropdownIndicator = ({ innerProps }) => {
    return (
      <div className="select-customizable__indicator " {...innerProps}>
        {iconTypeIndicator ? <Icon type={iconTypeIndicator} size="s" color="black" /> : <div className="select-customizable__dropdown-chevron" />}
      </div>
    );
  };

  const formatGroupLabel = (data) => (
    <div className="select-customizable__group-label">
      <span>{data.label}</span>
      <span className="select-customizable__group-badge">{data.options.length}</span>
    </div>
  );

  const MultiValueContainer = () => {
    return showValuesAsList ? null : undefined;
  };

  const ValueContainer = ({ children, hasValue, getValue }) => {
    if (!showValuesAsList || !hasValue) {
      return children;
    }

    const values = getValue();
    const displayValue = values
      .map((v) => (useShortLabels ? v.shortLabel : v.label))
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
    return (
      <div className="select-customizable__value-container">
        {displayValue}
        {children[1]} {/* Mantener el input para la interactividad */}
      </div>
    );
  };

  const getLabelTitle = (labelValue) => {
    if (typeof labelValue === 'string') return labelValue;

    // Si es un elemento React con children como string
    if (React.isValidElement(labelValue) && typeof labelValue.props.children === 'string') {
      return labelValue.props.children;
    }

    // Si es un elemento React con children como array (múltiples elementos)
    if (React.isValidElement(labelValue) && Array.isArray(labelValue.props.children)) {
      return labelValue.props.children.filter((child) => typeof child === 'string').join(' ');
    }

    return '';
  };

  const sharedProps = {
    _listboxId: listboxId,
    _focusedOptionId: focusedOptionId,
    _onOptionFocusChange: onOptionFocusChange,
    onKeyDown: handleKeyDown,
    onMenuOpen: handleMenuOpen,
    onMenuClose: handleMenuClose,
    tabSelectsValue: false,
  };

  const sharedStyles = {
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (provided, state) => ({
      ...provided,
      boxShadow: undefined,
      cursor: 'pointer',
      borderRadius: 'var(--select-customizable-border-radius)',
      transition: 'border-color 0.4s',
      ...getControlBorderStyles(state.isFocused, state.hasValue, finalIsSearchable, !!error),
    }),
    option: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      color: 'inherit',
    }),
  };

  return (
    <>
      {label && (
        <label
          className="select-customizable__label"
          title={getLabelTitle(label)}
          id={labelId}
        >
          {label} {props.required && <sup>*</sup>}
        </label>
      )}
      {canCreate ? (
        <CreatableSelect
          ref={selectRef}
          menuPosition={'fixed'}
          menuPlacement={finalMenuPlacement}
          options={options}
          className={`select-customizable select-customizable--${variant} select-customizable--size-${size}${error ? ' select-customizable--error' : ''}`}
          classNamePrefix="select-customizable"
          defaultValue={defaultValue}
          value={value}
          placeholder={placeholder}
          inputId={resolvedInputId}
          aria-labelledby={labelId}
          components={{
            DropdownIndicator,
            Control: AccessibleControl,
            Input: AccessibleInput,
            Menu: AccessibleMenu,
            Option: AccessibleOption,
          }}
          noOptionsMessage={() => noOptionsMessage}
          isMulti={isMulti}
          onChange={onChange}
          onCreateOption={onCreateOption}
          createOptionPosition="last"
          isClearable={true}
          formatCreateLabel={(text) => {
            return `${t('placeholder:Create new section')}: ${text}`;
          }}
          name={name}
          styles={sharedStyles}
          {...sharedProps}
        />
      ) : (
        <Select
          ref={selectRef}
          menuPosition={menuPosition}
          menuPlacement={finalMenuPlacement}
          options={options}
          className={`select-customizable select-customizable--${variant} select-customizable--size-${size}${error ? ' select-customizable--error' : ''}`}
          classNamePrefix={`${showValuesAsList ? 'select-customizable--show-values-as-list' : 'select-customizable'}`}
          defaultValue={defaultValue}
          value={value}
          placeholder={placeholder}
          inputId={resolvedInputId}
          aria-labelledby={labelId}
          components={{
            DropdownIndicator,
            Control: AccessibleControl,
            Input: AccessibleInput,
            Menu: AccessibleMenu,
            Option: AccessibleOption,
            ...(showValuesAsList && {
              MultiValue: MultiValueContainer,
              ValueContainer,
            }),
          }}
          noOptionsMessage={() => noOptionsMessage}
          isMulti={isMulti}
          onChange={onChange}
          name={name}
          menuPortalTarget={menuPortalTarget}
          formatGroupLabel={formatGroupLabel}
          hideSelectedOptions={hideSelectedOptions}
          formatOptionLabel={(data, { context }) => (useShortLabels && context === 'value' ? data.shortLabel : data.label)}
          styles={{
            ...sharedStyles,
            ...(showValuesAsList && {
              valueContainer: (base) => ({
                ...base,
                padding: '2px 8px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }),
            }),
          }}
          isSearchable={finalIsSearchable}
          {...sharedProps}
          {...props}
        />
      )}
      {error && <span className="select-customizable__error">{error}</span>}
    </>
  );
};

SelectCustomizable.propTypes = {
  isMulti: PropTypes.bool,
  options: PropTypes.array,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  noOptionsMessage: PropTypes.string,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(['default']),
  size: PropTypes.oneOf(['default', 'small']),
  name: PropTypes.string,
  inputId: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isSearchable: PropTypes.bool,
  iconTypeIndicator: PropTypes.string,
  menuPortalTarget: PropTypes.object,
  useShortLabels: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  showValuesAsList: PropTypes.bool,
  canCreate: PropTypes.bool,
  onCreateOption: PropTypes.func,
  menuPosition: PropTypes.string,
  error: PropTypes.string,
  defaultValue: PropTypes.any,
  value: PropTypes.any,
  t: PropTypes.func,
};

SelectCustomizable.defaultProps = {
  isMulti: false,
  options: [],
  noOptionsMessage: 'No hay opciones...',
  variant: 'default',
  size: 'default',
  menuPosition: 'fixed',
  onChange: () => {
    return null;
  },
};
export default withTranslation(['placeholder'])(SelectCustomizable);
